//@flow
import React, {useState} from 'react';
import {Button, makeStyles, TextField, Typography} from '@material-ui/core';

import {useForm} from 'react-hook-form';
import {colors} from '../../../assets/styles/colors';

export type DiscountType = 'percentage' | 'amount';

export type DiscountData = {
  type: DiscountType,
  value: number,
};

type Props = {
  maxPrice: number,
  onApplyDiscount: (data: DiscountData) => void,
  defaultDiscount?: number | string,
  defaultType?: DiscountType,
};

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  iconBtn: {
    width: '32px',
    height: '32px',
    minWidth: '32px',
    backgroundColor: colors.grey300,
    borderRadius: '5px',
    color: colors.grey600,
    aspectRatio: '1',
  },
  iconBtnActive: {
    backgroundColor: '#E4D6FF',
    color: colors.purple,
    fontWeight: 600,
  },
  discount: {
    height: '32px',
    padding: '0 4px',
    textAlign: 'end',
  },
  submit: {
    borderRadius: '5px',
    backgroundColor: colors.success,
    color: 'white',
    paddingInline: '32px',
  },
  errorMessage: {
    color: colors.error,
    fontSize: '0.75rem',
    marginTop: '4px',
    width: '100%',
  },
}));

const getErrorMessage = (type: DiscountType, value: number) => {
  if (type === 'percentage') return 'El no puede ser mayor a 100%.';

  return `El descuento no puede ser mayor a ${value}.`;
};

export default function DiscountForm(props: Props) {
  const {maxPrice, onApplyDiscount, defaultDiscount, defaultType} = props;

  const {register, handleSubmit, errors} = useForm();

  const [discountType, setDiscountType] = useState<DiscountType>(
    defaultType || 'amount'
  );

  const classes = useStyles();

  const onSubmit = ({discountAmount}) => {
    if (discountType === 'percentage') {
      const percentage = Number(discountAmount) / 100;
      onApplyDiscount({
        type: discountType,
        value: percentage,
      });
      return;
    }
    onApplyDiscount({type: discountType, value: Number(discountAmount)});
  };

  const maxDiscount = discountType === 'percentage' ? 100 : maxPrice;
  const maxDiscountError = getErrorMessage(discountType, maxPrice);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
        <Button
          size="small"
          className={`${classes.iconBtn} ${
            discountType === 'percentage' ? classes.iconBtnActive : ''
          }`}
          onClick={() => setDiscountType('percentage')}
        >
          %
        </Button>
        <Button
          size="small"
          className={`${classes.iconBtn} ${
            discountType === 'amount' ? classes.iconBtnActive : ''
          }`}
          onClick={() => setDiscountType('amount')}
        >
          $
        </Button>

        <TextField
          size="small"
          variant="outlined"
          name="discountAmount"
          defaultValue={defaultDiscount}
          placeholder="10"
          fullWidth
          inputProps={{className: classes.discount, step: '0.01'}}
          error={!!errors.discountAmount}
          inputRef={register({
            required: 'Campo obligatorio',
            max: {
              value: maxDiscount,
              message: maxDiscountError,
            },
            min: {
              value: 0,
              message: 'El descuento no puede ser menor a 0.',
            },
          })}
          type="number"
        />
        <Button size="small" className={classes.submit} type="submit">
          aplicar
        </Button>
      </form>
      {!!errors.discountAmount && (
        <Typography className={classes.errorMessage}>
          {errors.discountAmount.message}
        </Typography>
      )}
    </>
  );
}
